import React, { useState, useEffect, useCallback } from "react";
import { useUIVersion } from "../../contexts/UIVersionContext";
import { doc, updateDoc, setDoc, arrayUnion, getDoc } from "firebase/firestore";
import { useAuth } from "../../hooks/useAuth";
import { db } from "../../firebase";
import "./tracker-notes.css";

/**
 * Unified TrackerNotes component for cross-tracker notes system
 * 
 * Features:
 * - Notes persist across all trackers with source labels
 * - Auto-clear input on Enter (as requested)
 * - History modal showing notes from all trackers
 * - Real-time note saving
 * - Backward compatibility with existing notes
 */
export default function TrackerNotes({
  userId,
  trackerType = "generic",
  existingNotes = [], // Legacy notes from individual tracker
  onNotesUpdate = null,
  className = ""
}) {
  const { isFeatureEnabled } = useUIVersion();
  const { currentUser } = useAuth();
  const modernNotesEnabled = isFeatureEnabled('modernTrackerNotes');
  
  const [noteText, setNoteText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [allNotes, setAllNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Collection path for unified notes - only create if userId exists
  const userNotesRef = userId ? doc(db, 'user_notes', userId) : null;

  // Load all notes for this user from the unified collection
  const loadUnifiedNotes = useCallback(async () => {
    if (!modernNotesEnabled || !userId || !userNotesRef) return;
    
    setLoading(true);
    try {
      const docSnap = await getDoc(userNotesRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setAllNotes(data.notes || []);
      } else {
        setAllNotes([]);
      }
    } catch (error) {
      console.error("Error loading unified notes:", error);
      setAllNotes([]);
    } finally {
      setLoading(false);
    }
  }, [modernNotesEnabled, userId, userNotesRef]);

  // Load notes on component mount
  useEffect(() => {
    if (modernNotesEnabled) {
      loadUnifiedNotes();
    }
  }, [modernNotesEnabled, loadUnifiedNotes]);

  // Add a new note to the unified collection
  const addUnifiedNote = useCallback(async (text) => {
    if (!text.trim() || !userId || !currentUser || !userNotesRef) return false;

    setIsSubmitting(true);
    try {
      const noteData = {
        text: text.trim(),
        timestamp: new Date(),
        author: currentUser.displayName || currentUser.email || "Unknown",
        trackerSource: trackerType
      };

      await updateDoc(userNotesRef, {
        notes: arrayUnion(noteData)
      }).catch(async () => {
        // If document doesn't exist, create it using setDoc
        await setDoc(userNotesRef, {
          userId: userId,
          notes: [noteData]
        });
      });

      // Reload notes to get the updated list
      await loadUnifiedNotes();
      
      // Notify parent component if needed
      if (onNotesUpdate) {
        onNotesUpdate([noteData]);
      }

      return true;
    } catch (error) {
      console.error("Error adding unified note:", error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [userId, currentUser, trackerType, userNotesRef, loadUnifiedNotes, onNotesUpdate]);

  // Handle note submission (Enter key or button click)
  const handleSubmitNote = useCallback(async () => {
    if (!noteText.trim()) return;

    if (modernNotesEnabled) {
      const success = await addUnifiedNote(noteText);
      if (success) {
        setNoteText(""); // Auto-clear input as requested
      }
    } else {
      // Legacy mode - use existing notes system
      if (onNotesUpdate) {
        const noteData = {
          text: noteText.trim(),
          timestamp: new Date(),
          author: currentUser?.displayName || currentUser?.email || "Unknown"
        };
        onNotesUpdate([...existingNotes, noteData]);
        setNoteText(""); // Auto-clear input
      }
    }
  }, [noteText, modernNotesEnabled, addUnifiedNote, onNotesUpdate, existingNotes, currentUser]);

  // Handle Enter key press
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitNote();
    }
  }, [handleSubmitNote]);

  // Get notes for current tracker (for display in main area)
  const currentTrackerNotes = modernNotesEnabled 
    ? allNotes.filter(note => note.trackerSource === trackerType)
    : existingNotes;

  // Get recent note for display
  const recentNote = currentTrackerNotes
    .slice()
    .reverse()
    .find(note => note.text);

  // Format tracker source for display
  const formatTrackerSource = (source) => {
    const trackerNames = {
      '4x4': '4x4 Tracker',
      'licensing': 'Licensing',
      'production': 'Production',
      'associate': 'Associate',
      'prospect': 'Prospect'
    };
    return trackerNames[source] || source;
  };

  // Don't render if no userId (prevents Firebase errors)
  if (!userId) {
    return (
      <div className={`tracker-notes ${modernNotesEnabled ? 'modern' : 'legacy'} ${className}`}>
        <div className="notes-inline-add">
          <input
            className="note-input"
            type="text"
            placeholder="Please log in to add notes..."
            disabled={true}
          />
          <button
            className="notes-openall-emoji"
            disabled={true}
            title="Login required"
          >
            📋
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`tracker-notes ${modernNotesEnabled ? 'modern' : 'legacy'} ${className}`}>
      {/* Notes input area */}
      <div className="notes-inline-add">
        <input
          className="note-input"
          type="text"
          placeholder="Add a quick note... (Press Enter)"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
        />
        
        {/* History button (emoji) */}
        <button
          className="notes-openall-emoji"
          onClick={() => setShowHistory(true)}
          title="View all notes history"
          disabled={loading}
        >
          📋
        </button>
      </div>

      {/* Recent note display */}
      {recentNote && (
        <div className="note-meta-under">
          Last: {recentNote.text.substring(0, 50)}
          {recentNote.text.length > 50 ? '...' : ''}
          {modernNotesEnabled && recentNote.trackerSource !== trackerType && (
            <span className="note-source-tag">
              from {formatTrackerSource(recentNote.trackerSource)}
            </span>
          )}
        </div>
      )}

      {/* Notes history modal */}
      {showHistory && (
        <div className="notes-modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <h3>
                {modernNotesEnabled ? 'All Notes History' : `${formatTrackerSource(trackerType)} Notes`}
              </h3>
              <button 
                className="notes-modal-close"
                onClick={() => setShowHistory(false)}
              >
                ×
              </button>
            </div>
            
            <div className="notes-list-modal">
              {loading ? (
                <div className="notes-loading">Loading notes...</div>
              ) : (
                <>
                  {(modernNotesEnabled ? allNotes : existingNotes)
                    .slice()
                    .reverse()
                    .map((note, index) => (
                      <div key={index} className="note-item">
                        <div className="note-text">{note.text}</div>
                        <div className="note-meta">
                          {note.author} • {new Date(note.timestamp).toLocaleDateString()}
                          {modernNotesEnabled && note.trackerSource && (
                            <span className="note-source">
                              • {formatTrackerSource(note.trackerSource)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  
                  {(modernNotesEnabled ? allNotes : existingNotes).length === 0 && (
                    <div className="notes-empty">No notes yet</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}